import { FormEventHandler, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Modal,
} from '@mui/material';
import _ from 'lodash';

import { Button } from '../../shared/Button';
import { Icon } from '../../shared/Icon';
import { TagsInput } from '../../shared/TagsInput';
import { TEMPLATE_DISPLAY_NAMES } from '../model/PostTemplates';
import { PostTemplateSelector } from '../templates/PostTemplateSelector';
import { TemplateId } from '@lifeserver/shared';

import { usePortalTags } from '../../hooks/portal';

import styles from './CreatePlannedPostModal.module.scss';

export interface CreatePlannedPostModalProps {
    open: boolean;
    onPostCreate?: (
        postId: string,
        templateId: TemplateId,
        notes: string,
        tags: Array<{ name: string }>,
    ) => void;
    onClose?: () => void;
    startDate?: Date;
    endDate?: Date | null;
    allowedTemplates?: TemplateId[];
}

export const CreatePlannedPostModal = ({
    open,
    onPostCreate,
    onClose,
    startDate,
    endDate,
    allowedTemplates,
}: CreatePlannedPostModalProps) => {
    return (
        <Modal open={open} onClose={onClose}>
            <div className={styles.modalContainer}>
                {open && (
                    <CreatePlannedPostModalInner
                        onPostCreate={onPostCreate}
                        onClose={onClose}
                        startDate={startDate}
                        endDate={endDate}
                        allowedTemplates={allowedTemplates}
                    />
                )}
            </div>
        </Modal>
    );
};

interface CreatePlannedPostModalInnerProps {
    onPostCreate?: (
        postId: string,
        templateId: TemplateId,
        notes: string,
        tags: Array<{ name: string }>,
    ) => void;
    onClose?: () => void;
    startDate?: Date;
    endDate?: Date | null;
    allowedTemplates?: TemplateId[];
}

const CreatePlannedPostModalInner = ({
    onPostCreate,
    onClose,
    startDate,
    endDate,
    allowedTemplates,
}: CreatePlannedPostModalInnerProps) => {
    const [postId, setPostId] = useState('');
    const [templateId, setTemplateId] = useState<TemplateId>(
        allowedTemplates?.[0] ?? 'article-static-medium',
    );
    const [notes, setNotes] = useState('');
    const [selectedTags, setSelectedTags] = useState<Array<{ name: string }>>(
        [],
    );
    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

    const { data: allTags = [], isLoading } = usePortalTags();
    const tagOptions = useMemo(
        () => _.uniqBy([...allTags, ...selectedTags], 'name'),
        [allTags, selectedTags],
    );

    const handleTemplateChange = (newTemplateId: TemplateId) => {
        setTemplateId(newTemplateId);
        setIsAccordionExpanded(false);
    };

    const onSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        if (postId.trim() && templateId) {
            onPostCreate?.(postId.trim(), templateId, notes, selectedTags);
            onClose?.();
        }
    };

    const selectedTemplate = templateId
        ? TEMPLATE_DISPLAY_NAMES[templateId]
        : 'Choose Template';

    return (
        <div className={styles.modal}>
            <header className={styles.header}>
                <h1>Create New Planned Post</h1>
                <Button color="secondary" onClick={onClose}>
                    X
                </Button>
            </header>

            <form onSubmit={onSubmit} className={styles.form}>
                <div className={styles.field}>
                    <label>Start Date</label>
                    <p>
                        {startDate?.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </p>
                </div>

                <div className={styles.field}>
                    <label>End Date</label>
                    <p>
                        {endDate?.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        }) || 'No end date'}
                    </p>
                </div>

                <div className={styles.field}>
                    <label htmlFor="name">Name</label>
                    <input
                        id="name"
                        type="text"
                        value={postId}
                        onChange={(e) => setPostId(e.target.value)}
                        placeholder="Enter post name"
                        required
                        className={styles.input}
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="notes">Notes</label>
                    <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Enter post notes"
                        className={styles.textarea}
                    />
                </div>

                <div className={styles.field}>
                    <TagsInput
                        value={selectedTags}
                        onChange={setSelectedTags}
                        options={tagOptions}
                        loading={isLoading}
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="templateSelector">Template</label>
                    <Accordion
                        expanded={isAccordionExpanded}
                        onChange={(_, expanded) =>
                            setIsAccordionExpanded(expanded)
                        }
                    >
                        <AccordionSummary
                            expandIcon={<Icon name="expand_more" />}
                            className={
                                templateId ? styles.accordionSelected : ''
                            }
                        >
                            {selectedTemplate}
                        </AccordionSummary>
                        <AccordionDetails>
                            <PostTemplateSelector
                                value={templateId}
                                onChange={handleTemplateChange}
                                allowedTemplates={allowedTemplates}
                            />
                        </AccordionDetails>
                    </Accordion>
                </div>

                <footer className={styles.footer}>
                    <Button color="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!postId.trim() || !templateId}
                        color="primary"
                    >
                        Create Planned Post
                    </Button>
                </footer>
            </form>
        </div>
    );
};
